import { useState, useMemo, useEffect } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Search,
  Plus,
  Pencil,
  Trash2,
  Package,
  Boxes,
  LayoutGrid,
  TreePine,
  MoreHorizontal
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { StatusCard } from '@/components/dashboard/StatusCard';
import { useInventory } from '@/context/InventoryContext';
import {
  ProductItem,
  SENSOR_BOX_ID,
  CONTROL_BOX_ID,
} from '@/data/products';

type ViewMode = 'tree' | 'tabs';

// Box Kit (Assembly) Form Dialog - for creating new assemblies
function BoxKitFormDialog({
  open,
  onOpenChange,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: Partial<ProductItem>) => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name: formData.name,
      sku: formData.sku,
      manufacturer: '',
      partNumber: formData.sku,
      parentId: null,
      parentType: null,
      qtyPerSystem: 1,
      startOnHand: 0,
      unitCost: 0,
      type: 'assembly',
    });
    onOpenChange(false);
    setFormData({ name: '', sku: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Add New Box Kit</DialogTitle>
          <DialogDescription>
            Create a new assembly. You can add parts to it after creation.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="kit-name" className="text-right">
                Name
              </Label>
              <Input
                id="kit-name"
                placeholder="e.g., Sensor Box"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="kit-sku" className="text-right">
                Part Number
              </Label>
              <Input
                id="kit-sku"
                placeholder="e.g., ASSY-SENSOR-BOX"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                className="col-span-3"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Create Box Kit</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Component/Part Form Dialog - for adding parts to assemblies
function PartFormDialog({
  product,
  parentOptions,
  parentId,
  onSave,
  open,
  onOpenChange,
  suppliers,
}: {
  product?: ProductItem | null;
  parentOptions: ProductItem[];
  parentId?: number | null;
  onSave: (data: Partial<ProductItem>) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suppliers: { id: number; name: string }[];
}) {
  const [formData, setFormData] = useState<Partial<ProductItem>>({
    name: product?.name || '',
    sku: product?.sku || '',
    manufacturer: product?.manufacturer || '',
    partNumber: product?.partNumber || '',
    parentId: product?.parentId || parentId || null,
    qtyPerSystem: product?.qtyPerSystem || 1,
    startOnHand: product?.startOnHand || 0,
    unitCost: product?.unitCost || 0,
    type: product?.type || 'component',
  });

  // Update form data when product changes (for edit mode)
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        sku: product.sku || '',
        manufacturer: product.manufacturer || '',
        partNumber: product.partNumber || '',
        parentId: product.parentId || parentId || null,
        qtyPerSystem: product.qtyPerSystem || 1,
        startOnHand: product.startOnHand || 0,
        unitCost: product.unitCost || 0,
        type: product.type || 'component',
      });
    } else if (parentId) {
      setFormData(prev => ({ ...prev, parentId, type: 'component' }));
    }
  }, [product, parentId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parentType = formData.parentId === SENSOR_BOX_ID
      ? 'sensor_box'
      : formData.parentId === CONTROL_BOX_ID
        ? 'control_box'
        : null;
    onSave({ ...formData, parentType });
    onOpenChange(false);
  };

  const isEditMode = product && product.id !== 0;
  const isAssembly = product?.type === 'assembly';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? (isAssembly ? 'Edit Box Kit' : 'Edit Part') : 'Add New Part'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode ? 'Update details below.' : 'Add a new part to the assembly.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="sku" className="text-right">
                Part Number
              </Label>
              <Input
                id="sku"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                className="col-span-3"
                required
              />
            </div>
            {!isAssembly && (
              <>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="manufacturer" className="text-right">
                    Supplier
                  </Label>
                  <Select
                    value={formData.manufacturer}
                    onValueChange={(value) => setFormData({ ...formData, manufacturer: value })}
                  >
                    <SelectTrigger className="col-span-3">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.name}>
                          {supplier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="parent" className="text-right">
                    Parent Assembly
                  </Label>
                  <Select
                    value={formData.parentId?.toString() || ''}
                    onValueChange={(value) => setFormData({ ...formData, parentId: value ? Number(value) : null })}
                  >
                    <SelectTrigger className="col-span-3">
                      <SelectValue placeholder="Select parent assembly" />
                    </SelectTrigger>
                    <SelectContent>
                      {parentOptions.map((parent) => (
                        <SelectItem key={parent.id} value={parent.id.toString()}>
                          {parent.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="qtyPerSystem" className="text-right">
                    Qty/System
                  </Label>
                  <Input
                    id="qtyPerSystem"
                    type="number"
                    min="1"
                    value={formData.qtyPerSystem}
                    onChange={(e) => setFormData({ ...formData, qtyPerSystem: Number(e.target.value) })}
                    className="col-span-3"
                    required
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="startOnHand" className="text-right">
                    Start On Hand
                  </Label>
                  <Input
                    id="startOnHand"
                    type="number"
                    value={formData.startOnHand}
                    onChange={(e) => setFormData({ ...formData, startOnHand: Number(e.target.value) })}
                    className="col-span-3"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="unitCost" className="text-right">
                    Unit Cost ($)
                  </Label>
                  <Input
                    id="unitCost"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.unitCost}
                    onChange={(e) => setFormData({ ...formData, unitCost: Number(e.target.value) })}
                    className="col-span-3"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">{isEditMode ? 'Save Changes' : 'Add Part'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Component Row for Table Display
function ComponentRow({
  product,
  onEdit,
  onDelete,
}: {
  product: ProductItem;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <tr className="border-b hover:bg-muted/50">
      <td className="p-3 font-mono text-sm">{product.sku}</td>
      <td className="p-3">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-muted-foreground" />
          <span>{product.name}</span>
        </div>
      </td>
      <td className="p-3 text-muted-foreground">{product.manufacturer || '-'}</td>
      <td className="p-3 text-center font-medium">{product.qtyPerSystem}</td>
      <td className="p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={onDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

// Tree Node Component for Tree View
function TreeNode({
  product,
  components,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onAddComponent,
}: {
  product: ProductItem;
  components: ProductItem[];
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: (p: ProductItem) => void;
  onDelete: (p: ProductItem) => void;
  onAddComponent?: (parentId: number) => void;
}) {
  const isAssembly = product.type === 'assembly';

  return (
    <div className="border rounded-lg mb-2">
      {/* Parent row */}
      <div
        className={cn(
          'flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50',
          isExpanded && 'border-b bg-muted/30'
        )}
        onClick={isAssembly ? onToggle : undefined}
      >
        {isAssembly && (
          <span className="text-muted-foreground">
            {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </span>
        )}
        <Boxes className={cn('h-5 w-5', isAssembly ? 'text-primary' : 'text-muted-foreground')} />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{product.name}</span>
            <Badge variant={isAssembly ? 'default' : 'secondary'}>
              {isAssembly ? 'Assembly' : 'Component'}
            </Badge>
          </div>
          <div className="text-sm text-muted-foreground">
            Part Number: {product.sku} | {components.length} components
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAssembly && onAddComponent && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onAddComponent(product.id); }}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Part
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onEdit(product); }}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onDelete(product); }}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {/* Child components */}
      {isExpanded && components.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Part Number</th>
                <th className="text-left p-3 font-medium">Name</th>
                <th className="text-left p-3 font-medium">Manufacturer</th>
                <th className="text-center p-3 font-medium">Qty/System</th>
                <th className="p-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {components.map((component) => (
                <ComponentRow
                  key={component.id}
                  product={component}
                  onEdit={() => onEdit(component)}
                  onDelete={() => onDelete(component)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Components Table for Tab View
function ComponentsTable({
  components,
  assemblyName,
  onEdit,
  onDelete,
  onAddComponent,
}: {
  components: ProductItem[];
  assemblyName: string;
  onEdit: (p: ProductItem) => void;
  onDelete: (p: ProductItem) => void;
  onAddComponent?: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Boxes className="h-5 w-5 text-primary" />
              {assemblyName} Components
            </CardTitle>
            <CardDescription>
              {components.length} components in this assembly
            </CardDescription>
          </div>
          {onAddComponent && (
            <Button onClick={onAddComponent}>
              <Plus className="h-4 w-4 mr-2" />
              Add Part
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Part Number</th>
                <th className="text-left p-3 font-medium">Name</th>
                <th className="text-left p-3 font-medium">Manufacturer</th>
                <th className="text-center p-3 font-medium">Qty/System</th>
                <th className="p-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {components.map((component) => (
                <ComponentRow
                  key={component.id}
                  product={component}
                  onEdit={() => onEdit(component)}
                  onDelete={() => onDelete(component)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export function ProductsPage() {
  const { products, createProduct, updateProduct, deleteProduct: deleteProductApi, suppliers } = useInventory();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('tree');
  const [expandedAssemblies, setExpandedAssemblies] = useState<Set<number>>(new Set());
  const [deleteProductState, setDeleteProductState] = useState<ProductItem | null>(null);
  const [editProduct, setEditProduct] = useState<ProductItem | null>(null);
  const [addToParentId, setAddToParentId] = useState<number | null>(null);
  const [boxKitDialogOpen, setBoxKitDialogOpen] = useState(false);
  const { toast } = useToast();

  // Get parent assemblies
  const parentAssemblies = useMemo(() =>
    products.filter(p => p.type === 'assembly'),
    [products]
  );

  // Filter products by search
  const filteredProducts = useMemo(() => {
    if (!searchQuery) return products;
    const query = searchQuery.toLowerCase();
    return products.filter(
      p =>
        p.name.toLowerCase().includes(query) ||
        p.sku.toLowerCase().includes(query) ||
        p.manufacturer.toLowerCase().includes(query) ||
        p.partNumber.toLowerCase().includes(query)
    );
  }, [products, searchQuery]);

  // Group components by parent
  const componentsByParent = useMemo(() => {
    const map = new Map<number, ProductItem[]>();
    parentAssemblies.forEach(parent => {
      map.set(
        parent.id,
        filteredProducts.filter(p => p.parentId === parent.id)
      );
    });
    return map;
  }, [filteredProducts, parentAssemblies]);

  const toggleExpanded = (id: number) => {
    setExpandedAssemblies(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSaveProduct = async (data: Partial<ProductItem>) => {
    if (editProduct && editProduct.id !== 0) {
      // Update existing - instant save to API
      const success = await updateProduct(editProduct.id, data);
      if (success) {
        toast({
          title: 'Product updated',
          description: 'The product has been saved successfully.',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to save product.',
          variant: 'destructive',
        });
      }
      setEditProduct(null);
    } else {
      // Create new - instant save to API
      const newProductData: Omit<ProductItem, 'id'> = {
        sku: data.sku || '',
        name: data.name || '',
        description: data.description || '',
        manufacturer: data.manufacturer || '',
        partNumber: data.partNumber || '',
        parentId: data.parentId || null,
        parentType: data.parentType || null,
        qtyPerSystem: data.qtyPerSystem || 1,
        startOnHand: data.startOnHand || 0,
        used: 0,
        unitCost: data.unitCost || 0,
        type: data.type || 'component',
      };

      const result = await createProduct(newProductData);
      if (result.success) {
        toast({
          title: 'Product created',
          description: 'The new product has been saved successfully.',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create product.',
          variant: 'destructive',
        });
      }
    }
  };

  const handleDeleteProduct = async () => {
    if (!deleteProductState) return;

    const success = await deleteProductApi(deleteProductState.id);
    if (success) {
      toast({
        title: 'Product deleted',
        description: 'The product has been deleted successfully.',
      });
    } else {
      toast({
        title: 'Error',
        description: 'Failed to delete product.',
        variant: 'destructive',
      });
    }
    setDeleteProductState(null);
  };

  // Summary stats
  const stats = useMemo(() => ({
    totalProducts: products.length,
    assemblies: parentAssemblies.length,
    components: products.filter(p => p.type === 'component').length,
  }), [products, parentAssemblies]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold">Products & BOM</h2>
          <p className="text-muted-foreground">
            Manage product assemblies and components
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setBoxKitDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Box Kit
          </Button>
        </div>
      </div>

      {/* Stats - Modern Floating Design */}
      <div className="grid gap-6 sm:grid-cols-3">
        <StatusCard
          title="Total Products"
          value={stats.totalProducts.toString()}
          subtext={`${stats.totalProducts} in catalog`}
          trend={stats.totalProducts > 0 ? 'up' : 'neutral'}
          type="custom"
          icon={Package}
          iconColor="text-blue-500"
          iconBgColor="bg-blue-500/10"
          gradientColor="blue"
        />

        <StatusCard
          title="Assemblies"
          value={stats.assemblies.toString()}
          subtext={`${stats.assemblies} systems`}
          trend="neutral"
          type="custom"
          icon={Boxes}
          iconColor="text-purple-500"
          iconBgColor="bg-purple-500/10"
          gradientColor="purple"
        />

        <StatusCard
          title="Components"
          value={stats.components.toString()}
          subtext={`${stats.components} parts`}
          trend="neutral"
          type="custom"
          icon={LayoutGrid}
          iconColor="text-orange-500"
          iconBgColor="bg-orange-500/10"
          gradientColor="orange"
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground mr-2">View:</span>
          <Button
            variant={viewMode === 'tree' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('tree')}
          >
            <TreePine className="h-4 w-4 mr-1" />
            Tree
          </Button>
          <Button
            variant={viewMode === 'tabs' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('tabs')}
          >
            <LayoutGrid className="h-4 w-4 mr-1" />
            Tabs
          </Button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'tree' ? (
        <div className="space-y-2">
          {parentAssemblies
            .filter(p => filteredProducts.includes(p) || componentsByParent.get(p.id)?.length)
            .map((assembly) => (
              <TreeNode
                key={assembly.id}
                product={assembly}
                components={componentsByParent.get(assembly.id) || []}
                isExpanded={expandedAssemblies.has(assembly.id)}
                onToggle={() => toggleExpanded(assembly.id)}
                onEdit={(p) => setEditProduct(p)}
                onDelete={(p) => setDeleteProductState(p)}
                onAddComponent={(parentId) => setAddToParentId(parentId)}
              />
            ))}
        </div>
      ) : (
        <Tabs defaultValue="sensor_box" className="space-y-4">
          <TabsList>
            <TabsTrigger value="sensor_box">Sensor Box</TabsTrigger>
            <TabsTrigger value="control_box">Control Box</TabsTrigger>
          </TabsList>
          <TabsContent value="sensor_box">
            <ComponentsTable
              components={componentsByParent.get(SENSOR_BOX_ID) || []}
              assemblyName="Sensor Box"
              onEdit={(p) => setEditProduct(p)}
              onDelete={(p) => setDeleteProductState(p)}
              onAddComponent={() => setAddToParentId(SENSOR_BOX_ID)}
            />
          </TabsContent>
          <TabsContent value="control_box">
            <ComponentsTable
              components={componentsByParent.get(CONTROL_BOX_ID) || []}
              assemblyName="Control Box"
              onEdit={(p) => setEditProduct(p)}
              onDelete={(p) => setDeleteProductState(p)}
              onAddComponent={() => setAddToParentId(CONTROL_BOX_ID)}
            />
          </TabsContent>
        </Tabs>
      )}

      {/* Box Kit Dialog */}
      <BoxKitFormDialog
        open={boxKitDialogOpen}
        onOpenChange={setBoxKitDialogOpen}
        onSave={handleSaveProduct}
      />

      {/* Edit Dialog */}
      <PartFormDialog
        product={editProduct}
        parentOptions={parentAssemblies}
        onSave={handleSaveProduct}
        open={!!editProduct}
        onOpenChange={(open) => !open && setEditProduct(null)}
        suppliers={suppliers}
      />

      {/* Add Part Dialog */}
      <PartFormDialog
        parentId={addToParentId}
        parentOptions={parentAssemblies}
        onSave={(data) => {
          handleSaveProduct({ ...data, parentId: addToParentId });
          setAddToParentId(null);
        }}
        open={!!addToParentId}
        onOpenChange={(open) => !open && setAddToParentId(null)}
        suppliers={suppliers}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteProductState} onOpenChange={() => setDeleteProductState(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete{' '}
              <strong>{deleteProductState?.name}</strong>
              {deleteProductState?.type === 'assembly' && ' and all its components'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProduct}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
